import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Building2, ExternalLink, Rocket, Loader2, Shield, Check, Info, Sparkles, Flame, LayoutGrid, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import SEOHead from '@/components/SEOHead';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public Enterprise Partners directory at /partners.
 *
 * Two strata, visually unified:
 *   1. Verified Enterprise Partners (paid, Stripe-Connect-onboarded)
 *   2. Featured Partners (curated 3rd-party AI tools we promote via
 *      affiliate programs — Marblism/Dub.co etc.) — distinguished only by
 *      a small "Featured" marker and tasteful FTC-compliant disclosure.
 *
 * Buyers land here to browse both. SEO-optimised — one canonical page
 * targeting enterprise-intent queries.
 */
export default function Partners() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [partners, setPartners] = useState([]);
  const [total, setTotal] = useState(0);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active category filter for the Featured AI Tools grid.
  // `null` (or missing query param) means "show all". Persisted in the
  // URL so `/partners?category=SEO%20%26%20Marketing` is shareable, and
  // browser back/forward buttons restore the filter correctly.
  const activeCategory = searchParams.get('category');

  const setActiveCategory = (cat) => {
    const next = new URLSearchParams(searchParams);
    if (cat) next.set('category', cat);
    else next.delete('category');
    setSearchParams(next, { replace: true });
  };

  // "Share this category" — copies the current filtered URL to the
  // clipboard so the operator / buyers can paste a pre-filtered link
  // into an email or DM. Falls back to the older document.execCommand
  // path for browsers/webviews that don't expose navigator.clipboard
  // (older Safari, in-app browsers), so nobody hits a silent no-op.
  const copyFilteredLink = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Link copied — paste it anywhere');
    } catch {
      toast.error('Could not copy — select the URL from the address bar');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [paid, aff] = await Promise.all([
          axios.get(`${API_URL}/api/partners/directory?limit=48`),
          axios.get(`${API_URL}/api/affiliate-partners?limit=500`),
        ]);
        setPartners(paid.data.partners || []);
        setTotal(paid.data.total || 0);
        setAffiliates(aff.data.partners || []);
      } catch (err) {
        console.error('Partner directory load failed:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const combinedCount = total + affiliates.length;

  // Distinct categories present in the loaded affiliate list, sorted
  // alphabetically. Computed from the real data rather than hard-coded
  // so new partner categories surface automatically — the filter is
  // always in sync with what's actually displayable.
  const categoryCounts = useMemo(() => {
    const counts = new Map();
    for (const a of affiliates) {
      const c = (a.category || '').trim();
      if (!c) continue;
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [affiliates]);

  // Apply the active filter. If the admin deletes the only partner in a
  // category (making the URL-preserved ?category= stale) we gracefully
  // fall back to "all" instead of rendering an empty grid.
  //
  // Sort A→Z by display name so the directory is scannable. Falls back
  // to the original API order (display_order) only when names match — so
  // FEATURED-vs-regular ordering is no longer the user-visible sort key.
  const sortAlpha = (arr) =>
    [...arr].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );

  const visibleAffiliates = useMemo(() => {
    if (!activeCategory) return sortAlpha(affiliates);
    const filtered = affiliates.filter((a) => (a.category || '') === activeCategory);
    return sortAlpha(filtered.length > 0 ? filtered : affiliates);
  }, [affiliates, activeCategory]);

  // JSON-LD structured data — tells Google this is a curated directory
  // of organizations so partners surface in enterprise-AI queries.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Enterprise AI Partners Directory',
    url: 'https://theailedger.ai/partners',
    description:
      'Directory of verified Enterprise AI companies and featured AI tools on theailedger.ai.',
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: combinedCount,
      itemListElement: [
        ...partners.slice(0, 15).map((p, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          item: {
            '@type': 'Organization',
            name: p.company_name,
            url: p.website || `https://theailedger.ai/partners/${p.partner_id}`,
            description: p.description || undefined,
          },
        })),
        ...affiliates.slice(0, 10).map((a, idx) => ({
          '@type': 'ListItem',
          position: partners.length + idx + 1,
          item: {
            '@type': 'Organization',
            name: a.name,
            url: `https://theailedger.ai/go/${a.slug}`,
            description: a.description || undefined,
          },
        })),
      ],
    },
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEOHead
        title="Enterprise AI Partners Directory — theailedger.ai"
        description="Browse verified Enterprise AI companies and featured AI tools on theailedger.ai. SaaS brands, AI platforms, and partner companies trusted by thousands of buyers."
        path="/partners"
        keywords="enterprise AI subscriptions, AI SaaS partners, verified AI companies, enterprise AI marketplace, AI subscription directory"
        schema={schema}
      />

      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-40">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="partners-home-btn"
          >
            <Logo />
          </button>
          <Button
            onClick={() => navigate('/partners/apply')}
            className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
            data-testid="partners-apply-cta"
          >
            Become a Partner →
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(189,0,255,0.12),transparent_60%)]" />
        <div className="relative container mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-6">
            <Shield className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-sm text-[#00F0FF] font-semibold tracking-wide">VERIFIED ENTERPRISE PARTNERS</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-5 leading-tight" data-testid="partners-h1">
            AI companies<br />
            <span className="text-[#00F0FF]">
              buyers can trust
            </span>
          </h1>
          <p className="text-base sm:text-lg text-neutral-300 max-w-2xl mx-auto mb-8 leading-relaxed">
            Established AI companies, SaaS brands, and featured tools. Every listing below
            was manually reviewed and vetted by our team before being added.
          </p>
          {!loading && combinedCount > 0 && (
            <p className="text-sm text-neutral-500" data-testid="partners-total-count">
              {combinedCount} {combinedCount === 1 ? 'company' : 'companies'} currently listed
            </p>
          )}
        </div>
      </section>

      {/* Directory */}
      <main className="container mx-auto px-6 md:px-12 py-12 md:py-16">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin" />
          </div>
        ) : (partners.length === 0 && affiliates.length === 0) ? (
          <EmptyState navigate={navigate} />
        ) : (
          <>
            {partners.length > 0 && (
              <>
                <SectionHeader
                  icon={<Shield className="w-5 h-5 text-[#00F0FF]" />}
                  label="Verified Enterprise Partners"
                  count={partners.length}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16" data-testid="partners-grid">
                  {sortAlpha(partners).map((p) => (
                    <PartnerCard key={p.partner_id} partner={p} />
                  ))}
                </div>
              </>
            )}

            {affiliates.length > 0 && (
              <>
                <SectionHeader
                  icon={<Sparkles className="w-5 h-5 text-[#00F0FF]" />}
                  label="Featured AI Tools"
                  count={activeCategory ? visibleAffiliates.length : affiliates.length}
                />

                {/* Category filter pills — only renders if there are
                    multiple distinct categories (no point in a filter
                    that has only one choice). */}
                {categoryCounts.length > 1 && (
                  <div
                    className="flex flex-wrap items-center gap-2 mb-6"
                    data-testid="affiliates-category-filter"
                  >
                    <button
                      type="button"
                      onClick={() => setActiveCategory(null)}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                        !activeCategory
                          ? 'bg-[#00F0FF] text-black border-[#00F0FF]'
                          : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10 hover:border-white/20'
                      }`}
                      data-testid="affiliate-category-all"
                    >
                      <LayoutGrid className="w-3 h-3" />
                      All
                      <span className={`text-[10px] ${!activeCategory ? 'text-black/70' : 'text-neutral-500'}`}>
                        {affiliates.length}
                      </span>
                    </button>
                    {categoryCounts.map(([cat, count]) => {
                      const isActive = activeCategory === cat;
                      // Slugify the category for the testid so tests
                      // can target a specific pill (e.g. "seo-marketing").
                      const slug = cat.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setActiveCategory(cat)}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                            isActive
                              ? 'bg-[#00F0FF] text-black border-[#00F0FF]'
                              : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10 hover:border-white/20'
                          }`}
                          data-testid={`affiliate-category-${slug}`}
                        >
                          {cat}
                          <span className={`text-[10px] ${isActive ? 'text-black/70' : 'text-neutral-500'}`}>
                            {count}
                          </span>
                        </button>
                      );
                    })}

                    {/* "Share this filtered view" — only visible when a
                        real filter is active, so the default /partners
                        view stays clean. One click copies the URL. */}
                    {activeCategory && (
                      <button
                        type="button"
                        onClick={copyFilteredLink}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#00FF94]/30 bg-[#00FF94]/10 text-[#00FF94] hover:bg-[#00FF94]/20 transition-colors ml-1"
                        data-testid="affiliate-category-share-link"
                        title="Copy this filtered URL — paste it into an email, DM, or tweet"
                      >
                        <Link2 className="w-3 h-3" />
                        Copy link
                      </button>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="affiliates-grid">
                  {visibleAffiliates.map((a) => (
                    <AffiliateCard key={a.slug} partner={a} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Apply CTA */}
        <section className="mt-20 bg-[#00F0FF]/5 border border-[#00F0FF]/30 rounded-2xl p-8 md:p-12 text-center" data-testid="partners-apply-section">
          <Rocket className="w-12 h-12 text-[#00F0FF] mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-black mb-3">Is your company next?</h2>
          <p className="text-neutral-300 max-w-xl mx-auto mb-6">
            If you're an established AI company and your audience would love recurring access
            to what you've built, apply to become a Verified Partner.
          </p>
          <Button
            onClick={() => navigate('/partners/apply')}
            className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold px-8 py-6 rounded-xl text-base"
            data-testid="partners-apply-footer-btn"
          >
            Apply to become a Partner →
          </Button>
        </section>
      </main>

      {/* FTC-compliant affiliate disclosure (site-wide line in footer) */}
      <footer className="border-t border-white/10 py-8">
        <div className="container mx-auto px-6 md:px-12 text-center space-y-3">
          {affiliates.length > 0 && (
            <p className="text-xs text-neutral-500 max-w-2xl mx-auto" data-testid="affiliate-disclosure-footer">
              Some featured tools link to third-party products via partnership programs;
              theailedger.ai may earn a commission when you sign up through those links,
              at no extra cost to you.
            </p>
          )}
          <p className="text-xs text-neutral-600">
            © 2026 theailedger.ai — The Marketplace for AI Products
          </p>
        </div>
      </footer>
    </div>
  );
}

function SectionHeader({ icon, label, count }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-xl md:text-2xl font-bold">{label}</h2>
      </div>
      <span className="text-sm text-neutral-500">({count})</span>
      <div className="flex-1 h-px bg-white/5" />
    </div>
  );
}

function PartnerCard({ partner }) {
  const subLabel = {
    monthly: 'Monthly memberships',
    annual: 'Annual subs',
    both: 'Monthly + Annual',
    one_time: 'One-time',
    mixed: 'Mixed plans',
  }[partner.subscription_type] || 'Subscriptions';

  const externalHref = partner.website || null;

  const handleCardClick = () => {
    if (externalHref) {
      window.open(externalHref, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className="group bg-neutral-900/50 border border-white/10 hover:border-[#00F0FF]/50 rounded-2xl p-6 transition-all cursor-pointer"
      onClick={handleCardClick}
      role={externalHref ? 'link' : undefined}
      tabIndex={externalHref ? 0 : undefined}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && externalHref) {
          e.preventDefault();
          handleCardClick();
        }
      }}
      data-testid={`partner-card-${partner.partner_id}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/15 border border-white/10 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-[#00F0FF]" />
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#00FF94]">
          <Check className="w-3 h-3" /> Verified
        </span>
      </div>
      <h3 className="text-xl font-bold mb-2 group-hover:text-[#00F0FF] transition-colors">
        {partner.company_name}
      </h3>
      <p className="text-sm text-neutral-400 mb-4 line-clamp-3 min-h-[3.6em]">
        {partner.description || '—'}
      </p>
      <div className="flex items-center justify-between pt-4 border-t border-white/5">
        <span className="text-xs text-neutral-500">
          {subLabel}
        </span>
        <span className="text-xs text-neutral-500">
          {partner.product_count} {partner.product_count === 1 ? 'plan' : 'plans'}
        </span>
      </div>
      {partner.website && (
        <a
          href={partner.website}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mt-4 inline-flex items-center gap-1 text-xs text-[#00F0FF] hover:text-[#00C2CC]"
        >
          {partner.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

/**
 * Affiliate listing card. Visually consistent with PartnerCard so the
 * directory feels unified — social proof for buyers that real enterprise
 * brands are on the platform. FTC-compliant disclosure is surfaced
 * tastefully via a small "ⓘ Featured partner" tooltip on each card AND a
 * site-wide footer line (see Partners footer).
 */
function AffiliateCard({ partner }) {
  const navigate = useNavigate();
  // Card click now opens the dedicated storefront on theailedger.ai first
  // (/featured/:slug) so visitors see Marblism et al. as a first-class
  // tenant of our marketplace before the final outbound click.
  const storeHref = `/featured/${partner.slug}`;

  return (
    <div
      className="group relative bg-neutral-900/50 border border-white/10 hover:border-[#00F0FF]/50 rounded-2xl p-6 transition-all cursor-pointer"
      onClick={() => navigate(storeHref)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(storeHref);
        }
      }}
      data-testid={`affiliate-card-${partner.slug}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden p-1">
          {partner.logo_url ? (
            <img
              src={partner.logo_url}
              alt={partner.name}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          ) : (
            <Building2 className="w-6 h-6 text-[#00F0FF]" />
          )}
        </div>
        <div className="flex items-center gap-2">
          {/*
            Live social-proof pill. Backend hides counts below threshold
            (returns 0), so a truthy `clicks_this_week` always means the
            partner has earned the pill for this week.
          */}
          {partner.clicks_this_week ? (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94]"
              data-testid={`affiliate-clicks-pill-${partner.slug}`}
              title={`${partner.clicks_this_week} clicks in the last 7 days`}
            >
              <Flame className="w-3 h-3" />
              {partner.clicks_this_week} this week
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#00F0FF]">
            <Sparkles className="w-3 h-3" /> Featured
          </span>
        </div>
      </div>

      <h3 className="text-xl font-bold mb-1 group-hover:text-[#00F0FF] transition-colors">
        {partner.name}
      </h3>
      {partner.tagline && (
        <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
          {partner.tagline}
        </p>
      )}
      <p className="text-sm text-neutral-400 mb-4 line-clamp-3 min-h-[3.6em]">
        {partner.description}
      </p>

      {partner.customer_benefit && (
        <div className="mb-4 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] font-semibold">
          <Check className="w-3 h-3" /> {partner.customer_benefit}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-white/5">
        <span className="text-xs text-neutral-500">{partner.category}</span>
        {partner.website_display && (
          <span className="inline-flex items-center gap-1 text-xs text-[#00F0FF]">
            {partner.website_display}
            <ExternalLink className="w-3 h-3" />
          </span>
        )}
      </div>

      {/* Tasteful FTC disclosure — visible on hover + always present for screen readers */}
      <div
        className="absolute top-2 left-2 group/info"
        onClick={(e) => e.stopPropagation()}
        data-testid={`affiliate-disclosure-${partner.slug}`}
      >
        <Info
          className="w-3 h-3 text-neutral-600 hover:text-neutral-400 transition-colors"
          aria-label="Featured partner disclosure"
        />
        <div
          className="pointer-events-none absolute left-5 top-0 w-56 p-2.5 rounded-lg bg-black/95 border border-white/10 text-[11px] text-neutral-300 opacity-0 group-hover/info:opacity-100 transition-opacity z-10 shadow-xl"
          role="tooltip"
        >
          Featured partner — theailedger.ai may earn a commission when you sign up through this link, at no extra cost to you.
        </div>
      </div>

      {/* Invisible but accessible link for crawlers / keyboard users */}
      <a
        href={storeHref}
        onClick={(e) => e.stopPropagation()}
        className="sr-only"
        data-testid={`affiliate-go-link-${partner.slug}`}
      >
        View {partner.name}
      </a>
    </div>
  );
}

function EmptyState({ navigate }) {
  return (
    <div className="text-center py-20" data-testid="partners-empty-state">
      <Building2 className="w-16 h-16 text-neutral-700 mx-auto mb-4" />
      <h2 className="text-2xl font-bold mb-2">Directory launching soon</h2>
      <p className="text-neutral-400 mb-6 max-w-md mx-auto">
        We're currently onboarding the first wave of Verified Partners. Check back soon,
        or apply to be among the first.
      </p>
      <Button
        onClick={() => navigate('/partners/apply')}
        className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
      >
        Apply to become a Partner
      </Button>
    </div>
  );
}
